/**
 * WebSocket Provider — initializes real-time connection when user is authenticated
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import { useWebSocketStore, onNotification, onMessage, onAllocationUpdate, onCalendarUpdate, onAccessChanged, onMattersUpdate, onSupportUpdate } from '@/lib/websocket';
import { useHearingsStore } from '@/lib/hearingsStore';
import { useMattersStore } from '@/lib/mattersStore';
import { resyncPushSubscription } from '@/lib/push';

/**
 * The React Query keys that must refresh whenever a calendar event changes (a hearing,
 * conference or manual event advertises itself ONLY as `calendar:updated`). Kept as ONE
 * exported list so "what refreshes on a diary change" has a single definition the tests
 * lock — a new calendar-dependent surface adds its key here, nowhere else.
 */
export const CALENDAR_UPDATE_QUERY_KEYS: readonly (readonly string[])[] = [
  ['calendar'],
  ['hearing-readiness'],
  ['hearings-needing-attention'],
  ['rpt-readiness'],
  ['rpt-ops'],
  // "Next 24 Hours" Agenda Engine — makes a reschedule reflect on barrister + clerk
  // dashboards without a manual reload.
  ['me-agenda'],
  // Conference confirm/cancel emit ONLY calendar:updated, so the conference tray must refresh.
  ['conferences'],
  // Dashboard "upcoming deadlines this week" counts tasks + hearings; a hearing moved across
  // the week boundary changes that count (RC-3 M1). Cheap aggregate; safe to refresh.
  ['dashboard-metrics'],
  // The diary event workspace kept showing the old time/readiness after a reschedule, and a
  // meeting booked from the diary never appeared on the matter. `['calendar']` (below) is
  // read by NOTHING — these are the keys components actually subscribe to.
  ['diary-detail'],
  ['matter-calendar-events'],
  ['hearing-detail'],
  ['matter-hearings-health'],
  // Barrister delegates a task to the clerk → it never appeared on the clerk's Master Diary.
  ['clerk-diary-tasks'],
  ['diary-tasks'],
];

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const connect = useWebSocketStore((s) => s.connect);
  const disconnect = useWebSocketStore((s) => s.disconnect);
  const connected = useWebSocketStore((s) => s.connected);
  const queryClient = useQueryClient();

  const isAuthenticated = !!user && !!accessToken;
  const prevTokenRef = useRef<string | null>(null);
  const prevConnectedRef = useRef(connected);

  // Connect when authenticated, disconnect when logged out
  useEffect(() => {
    if (isAuthenticated) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, connect, disconnect]);

  // N6: push is bound to the login session that registered the device — re-bind this browser's
  // existing subscription to the current session once per sign-in (no prompt; no-op if not enabled).
  const userId = user?.id;
  useEffect(() => {
    if (isAuthenticated && userId) void resyncPushSubscription();
  }, [isAuthenticated, userId]);

  // When the access token is refreshed (401 auto-retry), reconnect the socket
  // so the server validates the new token. Stale-token WS connections would
  // fail server-side socket middleware after the token expires.
  useEffect(() => {
    if (prevTokenRef.current && accessToken && prevTokenRef.current !== accessToken && isAuthenticated) {
      disconnect();
      connect();
    }
    prevTokenRef.current = accessToken ?? null;
  }, [accessToken, isAuthenticated, connect, disconnect]);

  // On reconnect after a dropout, re-fetch anything that could have changed
  // while the socket was down — missed events are never replayed by Socket.io.
  useEffect(() => {
    if (!prevConnectedRef.current && connected && isAuthenticated) {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['allocations'] });
      queryClient.invalidateQueries({ queryKey: ['conferences'] });
      queryClient.invalidateQueries({ queryKey: ['messaging', 'threads'] });
      // Socket.io never replays what was missed while the socket was down, so a Support reply
      // that arrived during the dropout would otherwise sit unseen until a manual reload. The
      // list key is a prefix of the detail key, so this recovers both.
      queryClient.invalidateQueries({ queryKey: ['support', 'mine'] });
    }
    prevConnectedRef.current = connected;
  }, [connected, isAuthenticated, queryClient]);

  // Handle real-time notification events
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubNotification = onNotification(() => {
      // Invalidate notifications query to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    });

    return () => {
      unsubNotification();
    };
  }, [isAuthenticated, queryClient]);

  // Handle real-time message events
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubMessage = onMessage((message) => {
      queryClient.invalidateQueries({ queryKey: ['messaging', 'messages', message.threadId] });
      queryClient.invalidateQueries({ queryKey: ['messaging', 'threads'] });
    });

    return () => {
      unsubMessage();
    };
  }, [isAuthenticated, queryClient]);

  // Handle allocation state-change events (offer/accept/decline/conference/withdraw)
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsub = onAllocationUpdate(() => {
      queryClient.invalidateQueries({ queryKey: ['allocations'] });
      queryClient.invalidateQueries({ queryKey: ['conferences'] });
    });

    return () => unsub();
  }, [isAuthenticated, queryClient]);

  // Handle calendar create/update/delete events
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsub = onCalendarUpdate(() => {
      // A hearing/conference/meeting change advertises itself ONLY as calendar:updated. The
      // hearings store is fetched once on mount, so refresh it here too — otherwise the
      // dashboard "Upcoming/Today Hearings" widgets went stale while the diary next to them
      // updated (a visible self-contradiction). Then invalidate every calendar-dependent
      // read model from the single canonical list.
      void useHearingsStore.getState().fetchHearings();
      for (const queryKey of CALENDAR_UPDATE_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey: queryKey as string[] });
      }
    });

    return () => unsub();
  }, [isAuthenticated, queryClient]);

  // Handle matter lifecycle events (create/update/close/restore/archive/delete/assign).
  // Previously only three list pages subscribed locally; the React-Query-cached matter
  // DETAIL (['matter-by-id']) was never invalidated, so a matter closed/deleted while its
  // detail page was open stayed stale until manual refresh. Invalidate both here.
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsub = onMattersUpdate((payload) => {
      // The matters STORE, not just the query cache — mirroring the hearings refetch above.
      //
      // Matter detail and the dashboards read `useMattersStore`, and MatterDetailPage does
      // `matter = storeMatter ?? apiFetchedMatter` with the ['matter-by-id'] query DISABLED
      // whenever the store already holds it. So invalidating query keys alone changed
      // nothing on the page the user was actually looking at: a clerk could rename a matter,
      // reassign counsel or change its status and the barrister's open matter page kept the
      // old header indefinitely — while its hearings panel updated live beside it.
      // Verified in a browser before this line existed: the open page stayed stale.
      void useMattersStore.getState().fetchMatters();

      // Every matter-derived READ-MODEL, so no surface contradicts another after a matter
      // changes — e.g. "Open Matters: 3" must become 2 the instant a matter is closed, and
      // the urgent/attention queues, aged debt, workload, alerts and money widgets must
      // agree. This bounded set is refreshed on EVERY matters:updated (incl. the payment /
      // document / post-hearing emits that carry no `event`), so it never storms.
      const MATTER_READ_MODELS = [
        ['matters'], ['matter-by-id'],
        ['dashboard-metrics'], ['me-operational-alerts'], ['operational-alerts'],
        ['barrister-workload'], ['hearings-needing-attention'], ['activity-totals-mtd'],
        ['aged-debt'], ['money-requiring-attention'], ['ops-requiring-attention'],
        ['matter-financial-summary'], ['matter-financial-state'],
        // Money read-models keyed differently per page — a fee-note send / payment / credit
        // now emits matters:updated, so these must refresh too (clerk ops-aged-debt + barrister
        // my-* lists were never invalidated; the financial reports stayed on stale-time).
        ['ops-aged-debt'], ['my-aged-debt'], ['my-fee-notes'], ['fee-notes'],
        ['rpt-fin-ov'], ['rpt-of'], ['rpt-fees'], ['rpt-financial'],
        // Document lists — matters:updated also carries document_* sources (upload/update/
        // delete/version/restore). Without these keys a doc change never refreshed any list.
        ['matter-documents'], ['documents'], ['documents-search'], ['document-versions'],
        // Matter correspondence tab — refreshed when a matter email is sent (email_sent).
        ['matter-synced-emails'],
        // Task lists — a task change (source:task) refreshes the matter task tab + task pages.
        ['matter-tasks'], ['tasks'], ['diary-tasks'],
        // "Next 24 Hours" agenda also composes tasks due + matter-linked events.
        ['me-agenda'],
        // ── Keys that NOTHING in the repo invalidated, each producing a visible
        // self-contradiction on a single screen (Rule #14):
        //   matter-tasks-health   tick a task off → Tasks tab says done, the Health card
        //                         200px above still counts it open
        //   matter-intelligence   the timeline shows today's entry while the panel beside
        //                         it says "No activity for N days"
        //   matter-hearings-health  Hearings tab shows the new date, Health card scores the old
        //   matter-payments       record a payment → totals move, the Payments list on the
        //                         same tab does not show it
        //   matter-roles          counsel returns instructions → the card says they are off
        //                         the record, the panel above still names them Lead
        ['matter-tasks-health'], ['matter-intelligence'], ['matter-hearings-health'],
        ['matter-payments'], ['matter-payment-story'], ['matter-fees-activities'],
        ['matter-roles'], ['matter-fees'], ['hearing-readiness'],
        // Reports "Recovery" tab kept listing a paid note as overdue while Outstanding Fees
        // on the SAME page dropped it.
        ['rpt-overdue-notes'], ['rpt-hlth'], ['rpt-work'], ['rpt-clerk-ops'],
        // Contacts are Zustand-backed; these query keys are what the pickers/search render.
        ['contacts'], ['contacts-search'],
      ];
      for (const queryKey of MATTER_READ_MODELS) queryClient.invalidateQueries({ queryKey });

      // A confirmed lifecycle TRANSITION (rare + high-consequence) can ripple into second-
      // order consumers we don't enumerate above (reports, analytics). Refresh all active
      // queries only then — same pattern as onAccessChanged. Plain field edits / payment /
      // document emits (no matching event) never reach this, so there is no refetch storm.
      const TRANSITIONS = [
        'MatterClosed', 'MatterDeleted', 'MatterRestored', 'MatterArchived',
        'MatterAssigned', 'MatterUnassigned', 'MatterCreated',
      ];
      if (payload?.event && TRANSITIONS.includes(payload.event)) {
        queryClient.invalidateQueries();
      }
    });

    return () => unsub();
  }, [isAuthenticated, queryClient]);

  // Handle support ticket updates — Support has replied to a ticket this member raised.
  //
  // Refresh only; NEVER mark anything read. The badge on "My support tickets" counts replies
  // from Support that the member has not answered, and the count is derived on the server
  // (conversationSignals). Re-running the list query is what makes the badge correct — opening
  // the conversation, or merely receiving this event, must not clear it.
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsub = onSupportUpdate((event) => {
      queryClient.invalidateQueries({ queryKey: ['support', 'mine'] });
      queryClient.invalidateQueries({ queryKey: ['support', 'mine', event.ticketId] });
    });

    return () => unsub();
    // `connected` is in the dependency list deliberately. onSupportUpdate() binds to whatever
    // socket instance the store holds AT SUBSCRIPTION TIME, and a token refresh replaces that
    // instance (disconnect() then connect(), above). Without re-binding, this handler would stay
    // attached to the discarded socket and Support would silently stop updating after the first
    // token refresh of a session. See the note in the report — the sibling handlers above have
    // the same exposure and are left alone here.
  }, [isAuthenticated, connected, queryClient]);

  // Handle clerk access-change events — the barrister changed this clerk's
  // permissions. Re-scope the entire session so every scoped view (matters,
  // hearings, diary, documents, fees, search) and the clerk's navigation
  // (clerk-workspace) refetch under the new access — no refresh, no re-login.
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsub = onAccessChanged(() => {
      queryClient.invalidateQueries(); // refetch all active queries
    });

    return () => unsub();
  }, [isAuthenticated, queryClient]);

  return <>{children}</>;
}
